import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Subject} from 'rxjs';

export type SessionTimeoutReason = 'idle_timeout' | 'absolute_timeout';

export interface SessionExpiryWarning {
  remainingSeconds: number;
  reason: SessionTimeoutReason;
}

@Injectable({providedIn: 'root'})
export class SessionTimeoutService {
  private readonly warningSubject = new BehaviorSubject<SessionExpiryWarning | null>(null);
  private readonly expiredSubject = new Subject<SessionTimeoutReason>();
  private warningTimer?: ReturnType<typeof setTimeout>;
  private countdownTimer?: ReturnType<typeof setInterval>;
  private deadline = 0;
  private reason: SessionTimeoutReason = 'idle_timeout';

  readonly warning$ = this.warningSubject.asObservable();
  readonly expired$ = this.expiredSubject.asObservable();

  updateFromResponse(response: HttpResponse<unknown> | HttpErrorResponse): void {
    const remainingSeconds = Number(response.headers?.get('X-Session-Expires-In'));
    const warningSeconds = Number(response.headers?.get('X-Session-Warning-Seconds'));
    const reason = response.headers?.get('X-Session-Timeout-Reason');
    if (!Number.isFinite(remainingSeconds) || remainingSeconds < 0
      || !Number.isFinite(warningSeconds) || warningSeconds < 1
      || !['idle_timeout', 'absolute_timeout'].includes(reason || '')) {
      return;
    }

    this.clearTimers();
    this.reason = reason as SessionTimeoutReason;
    this.deadline = this.now() + (remainingSeconds * 1000);
    const warningDelay = Math.max(0, (remainingSeconds - warningSeconds) * 1000);
    this.scheduleWarning(warningDelay);
  }

  clear(): void {
    this.clearTimers();
    this.warningSubject.next(null);
    this.deadline = 0;
  }

  private scheduleWarning(delay: number): void {
    if (delay === 0) {
      this.startCountdown();
      return;
    }
    const maximumDelay = 2147483647;
    if (delay > maximumDelay) {
      this.warningTimer = setTimeout(() => this.scheduleWarning(delay - maximumDelay), maximumDelay);
      return;
    }
    this.warningTimer = setTimeout(() => this.startCountdown(), delay);
  }

  private startCountdown(): void {
    const update = () => {
      const remainingSeconds = Math.max(0, Math.ceil((this.deadline - this.now()) / 1000));
      this.warningSubject.next({remainingSeconds, reason: this.reason});
      if (remainingSeconds === 0) {
        this.clearTimers();
        this.expiredSubject.next(this.reason);
      }
    };
    update();
    if (this.deadline > this.now()) {
      this.countdownTimer = setInterval(update, 1000);
    }
  }

  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    this.warningTimer = undefined;
    this.countdownTimer = undefined;
  }

  private now(): number {
    return typeof performance === 'undefined' ? Date.now() : performance.now();
  }
}
